import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { FilterOptions } from '../types';
import SplashScreen from '../components/SplashScreen';

export const MainLayout: React.FC = () => {
  const location = useLocation();
  
  // Derive activeTab from URL path
  const getTabFromPath = (pathname: string) => {
    const path = pathname.replace('/', '');
    if (!path || path === 'overview') return 'overview';
    if (path === 'all-recos') return 'all';
    if (path === 'new-recos') return 'new';
    return path; // buy, sell, hold, high-upside, high-downside, brokerages
  };

  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname));
  const [filters, setFilters] = useState<FilterOptions>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Splash screen: show once per session, allow Esc to skip
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return sessionStorage.getItem('splashShown') ? false : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!showSplash) return;

    const timer = setTimeout(() => {
      setShowSplash(false);
      try { sessionStorage.setItem('splashShown', '1'); } catch {}
    }, 1400); // show for 1.4s

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearTimeout(timer);
        setShowSplash(false);
        try { sessionStorage.setItem('splashShown', '1'); } catch {}
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [showSplash]);

  // Update activeTab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFiltersChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
  };

if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <Header
        onSearch={handleSearch}
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        searchQuery={searchQuery}
      />
      
      {/* Sidebar */}
      <aside
        className={`
          fixed top-20 left-0 z-30 w-72 h-[calc(100vh-5rem)] bg-obsidian border-r border-gray-200
          transform transition-transform duration-300 ease-in-out shadow-lg
          md:translate-x-0
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <Navigation
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setIsSidebarOpen(false);
            }}
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="md:ml-72 min-h-screen bg-obsidian">
        <Outlet context={{ filters, searchQuery, activeTab }} />
      </main>
    </div>
  );
};